import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiDetailFields,
  MiFileUpload,
  MiIcon,
  MiLoader,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_VARIANTS,
  COLORS,
  FILE_MIME_TYPE_EXTENSIONS,
} from "@miplatform/constants";
import { mdiEmailOutline, mdiEyeOutline } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { getAttachmentsFromFiles } from "./util/emailFormUtil";
import { EmailPreviewModal } from "./EmailPreviewModal/EmailPreviewModal";

export const EmailForm = ({
  showHeader = false,
  viewModel,
  entityType,
  containerStyle = {},
  formContainerStyle = {},
}) => {
  const { classes, theme } = useStyles();
  const {
    entity,
    templateType,
    isLoading,
    isConfirmModalOpen,
    isPreviewOpen,
    emailFields,
    documentCategory,
    account,
    color,
    recipient,
    title,
    files,
    handleFileChange,
    handlePreviewOpen,
    modalDescription,
    sendEmail,
    handlePreviewClose,
    handleConfirmModalClose,
    getValues,
  } = viewModel;

  return (
    <Box sx={containerStyle}>
      {showHeader && (
        <Box
          sx={{
            backgroundColor: theme.palette[color].light,
            borderBottom: `1px solid ${theme.palette[color].dark}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: 36,
            padding: theme.spacing(1, 4),
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <MiIcon path={mdiEmailOutline} color={theme.palette.primary.main} />
            <Typography
              className={classes.title}
            >{`To ${recipient}: ${title}`}</Typography>
          </Box>
          <MiButton
            variant={BUTTON_VARIANTS.OUTLINED}
            color={COLORS.BLACK}
            size="small"
            className={classes.emailPreviewButton}
            startIcon={{ path: mdiEyeOutline, iconSize: 0.6 }}
            title="View Email Preview"
            onClick={handlePreviewOpen}
          />
        </Box>
      )}
      <Box className={classes.container} sx={formContainerStyle}>
        <Box sx={{ position: "absolute", top: 0, left: 0, right: 0 }}>
          <MiLoader type="linear" isLoading={isLoading} />
        </Box>
        <MiDetailFields detailFields={emailFields} />
        <Box px={1} pb={4}>
          <MiFileUpload
            dragAndDrop
            disabled={!documentCategory}
            validFileTypes={documentCategory?.allowedFileFormats || []}
            onChange={(files) => handleFileChange(files)}
            acceptableFormatText={`Acceptable formats: ${(
              documentCategory?.allowedFileFormats || []
            )
              .map((mimeType) => FILE_MIME_TYPE_EXTENSIONS[mimeType])
              .join(", ")}`}
          />
        </Box>
      </Box>
      <EmailPreviewModal
        account={account}
        open={isPreviewOpen}
        entity={entity}
        entityType={entityType}
        fields={getValues()}
        attachments={getAttachmentsFromFiles(files)}
        templateType={templateType}
        onClose={handlePreviewClose}
      />
      <MiModalConfirm
        title={`Confirm: ${title}`}
        titleIcon={{
          path: mdiEmailOutline,
          color: theme.palette.primary.main,
        }}
        disableClick={isLoading}
        textPositive={`Send ${title}`}
        open={isConfirmModalOpen}
        handleNegative={handleConfirmModalClose}
        handlePositive={sendEmail}
        description={modalDescription}
        buttonPositiveColor={COLORS.PRIMARY}
      />
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  header: {
    borderLeft: `${theme.spacing(2)} solid ${theme.palette.grey.accent}`,
    boxSizing: "content-box",
    overflow: "auto",
  },
  title: {
    fontWeight: theme.fontWeight.semiBold,
    fontSize: theme.typography.pxToRem(14),
  },
  container: {
    position: "relative",
    padding: theme.spacing(4, 4, 0, 4),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    width: "100%",
  },
  emailPreviewButton: {
    padding: theme.spacing(0, 2),
    borderColor: theme.palette.common.black,
    borderRadius: theme.spacing(0.5),
    backgroundColor: theme.palette.common.white,
  },
}));
